import { DIALOG_DATA } from '@angular/cdk/dialog';
import {
    AfterViewInit,
    Component,
    ElementRef,
    HostListener,
    Inject,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { CodaltComponent } from 'app/codalt.component';
import { Workarea } from 'app/workarea.class';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import { Subscription } from 'rxjs';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

@Component({
    selector: 'app-image-viewer',
    templateUrl: './image-viewer.component.html',
    styleUrl: './image-viewer.component.scss',
    standalone: false,
})
export class ImageViewerComponent extends CodaltComponent implements OnInit, AfterViewInit, OnDestroy {
    zoomlevel = 1;

    images: ImageData[];
    workarea: Workarea;
    pdfDoc: any;
    currentPdfPage = 1;
    totalPdfPages = 0;
    pdfPages: any[] = [];
    pdfLoading = false;
    pdfError: string | null = null;
    hideTime = false;

    @ViewChildren('pdfCanvas') pdfCanvases!: QueryList<ElementRef<HTMLCanvasElement>>;
    @ViewChild('swiperRef') swiperComponent?;

    private canvasSubscription?: Subscription;
    constructor(
        public dialogRef: MatDialogRef<ImageViewerComponent>,
        @Inject(DIALOG_DATA) public data: {
            images: ImageObject[];
            currentImage: ImageObject;
            singleImage?: string;
            singlePdf?: string;
            workarea?: Workarea;
        }
    ) {
        super();
    }

    ngOnInit(): void {
        if (this.data.workarea) {
            this.workarea = this.data.workarea;
            this.hideTime = this.workarea.type === 'drillings';
        }
        if (this.data.singlePdf) {
            this.loadPdf(this.data.singlePdf);
        } else if (this.data.singleImage) {
            this.images = [];
        } else {
            const index = this.data.images.findIndex(img => img.id === this.data.currentImage.id);
            const images = this.data.images.map(image => {
                return {
                    image: image.image_url,
                    imageObject: image,
                };
            });
            this.images = [...images.slice(index, images.length), ...images.slice(0, index)];
        }
    }

    onScroll(event: WheelEvent) {
        // For PDFs, let Swiper handle the navigation
        if (!this.data.singlePdf) {
            // Original zoom functionality for images only
            if (event.deltaY < 0) {
                this.zoomlevel = this.zoomlevel + 1;
                this.swiperComponent?.nativeElement.swiper.zoom.in(this.zoomlevel);
            } else {
                if (this.zoomlevel > 1) {
                    this.zoomlevel = this.zoomlevel - 1;
                    this.swiperComponent?.nativeElement.swiper.zoom.in(this.zoomlevel);
                } else {
                    this.swiperComponent?.nativeElement.swiper.zoom.out();
                }
            }
        }
    }

    ngAfterViewInit(): void {
        this.subscriptions.add(
            this.dialogRef.afterOpened().subscribe(() => this.swiperComponent?.nativeElement.swiper?.update())
        );

        // Subscribe to changes in pdfCanvases ViewChildren
        if (this.data.singlePdf) {
            this.canvasSubscription?.unsubscribe();

            this.canvasSubscription = this.pdfCanvases.changes.subscribe(() => {
                if (this.pdfDoc && this.pdfCanvases.length > 0) {
                    setTimeout(() => this.renderAllPdfPages(), 0);
                }
            });

            // Also check if canvases are already available
            if (this.pdfDoc && this.pdfCanvases && this.pdfCanvases.length > 0) {
                setTimeout(() => this.renderAllPdfPages(), 0);
            }
        }
    }

    ngOnDestroy(): void {
        this.canvasSubscription?.unsubscribe();
        super.ngOnDestroy();
    }

    onSlideChange(event: any) {
        // Update current page when slide changes for PDFs
        if (this.data.singlePdf) {
            this.currentPdfPage = event.detail[0].activeIndex + 1;
        }
    }

    loadPdf(url: string) {
        this.pdfLoading = true;
        this.pdfError = null;
        GlobalWorkerOptions.workerSrc = `assets/pdfjs/pdf.worker.mjs`;

        const loadingTask = getDocument(url);
        loadingTask.promise
            .then(pdf => {
                this.pdfDoc = pdf;
                this.totalPdfPages = pdf.numPages;
                this.currentPdfPage = 1;

                // Create array of all pages for swiper
                this.pdfPages = [];
                for (let i = 1; i <= this.totalPdfPages; i++) {
                    this.pdfPages.push(i);
                }

                this.pdfLoading = false;

                // Check if ViewChildren are already available
                if (this.pdfCanvases && this.pdfCanvases.length > 0) {
                    setTimeout(() => this.renderAllPdfPages(), 0);
                }
            })
            .catch(() => {
                this.pdfError = 'Failed to load PDF document';
                this.pdfLoading = false;
            });
    }

    renderAllPdfPages() {
        if (!this.pdfDoc || !this.pdfCanvases || this.pdfCanvases.length === 0) {
            return;
        }

        const canvasElements = this.pdfCanvases.toArray();

        // Render each page to its corresponding canvas
        for (let i = 0; i < this.totalPdfPages && i < canvasElements.length; i++) {
            this.renderPdfPageToCanvas(i + 1, canvasElements[i].nativeElement);
        }
    }

    renderPdfPageToCanvas(pageNumber: number, canvas: HTMLCanvasElement) {
        if (!this.pdfDoc || pageNumber < 1 || pageNumber > this.totalPdfPages) {
            return;
        }

        this.pdfDoc
            .getPage(pageNumber)
            .then(page => {
                const scale = 1;
                const viewport = page.getViewport({ scale });

                const context = canvas.getContext('2d')!;
                canvas.height = viewport.height;
                canvas.width = viewport.width;

                const renderContext = {
                    canvasContext: context,
                    viewport: viewport,
                    canvas: canvas,
                };

                page.render(renderContext);
            })
            .catch(() => {
                // Error rendering PDF page - fail silently
            });
    }

    @HostListener('click', ['$event'])
    public click(event) {
        // Don't close dialog if clicking on PDF controls
        if (event?.target?.closest('.pdf-controls')) {
            return;
        }

        if (
            event?.target?.localName !== 'img' &&
            event?.target?.localName !== 'canvas' &&
            event?.srcElement?.className.indexOf('swiper-zoom-container') !== -1
        ) {
            this.dialogRef.close();
        }
    }
}

export interface ImageObject {
    id?: number;
    image_url: string;
    [key: string]: any; // Allow additional properties
}

export interface ImageData {
    image: string;
    imageObject: ImageObject;
}
